import { useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiErrorMessage } from '@/lib/api';
import { CATEGORY_KIND_LABELS } from '@shared/labels';
import type { CategoryKind } from '@shared/types';
import { useArchiveCategory, useCategories, useCreateCategory } from '@/hooks/useCategories';

export function CategoriesPage() {
  const { data: categories } = useCategories();
  const createCategory = useCreateCategory();
  const archiveCategory = useArchiveCategory();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<CategoryKind>('EXPENSE');

  const grouped = { INCOME: categories?.filter((c) => c.kind === 'INCOME') ?? [], EXPENSE: categories?.filter((c) => c.kind === 'EXPENSE') ?? [] };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Categorías</h1>
        <p className="text-sm text-muted-foreground">Tuyas — cada uno organiza las suyas.</p>
      </div>

      <form
        className="card flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          try {
            await createCategory.mutateAsync({ kind, name: name.trim() });
            setName('');
            toast.success('Categoría creada');
          } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo crear la categoría'));
          }
        }}
      >
        <div className="flex-1">
          <label className="text-xs font-medium text-muted-foreground">Nombre</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mascotas" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Tipo</label>
          <Select value={kind} onChange={(e) => setKind(e.target.value as CategoryKind)}>
            {Object.entries(CATEGORY_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <Button type="submit" disabled={createCategory.isPending}>
          Crear
        </Button>
      </form>

      {(['INCOME', 'EXPENSE'] as const).map((k) => (
        <div key={k} className="card">
          <h2 className="mb-3 text-sm font-medium">{CATEGORY_KIND_LABELS[k]}</h2>
          {grouped[k].length > 0 ? (
            <ul className="space-y-2">
              {grouped[k].map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">{c.name}</span>
                  <button
                    type="button"
                    aria-label="Eliminar"
                    className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-destructive"
                    onClick={() => archiveCategory.mutate(c.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Sin categorías todavía.</p>
          )}
        </div>
      ))}
    </div>
  );
}
